'use strict';

import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const SELECTOR_STACK = '.tc-website-image-scroll-stack';
const SELECTOR_ITEM = '.tc-website-imagescrollstackitem';
const SELECTOR_SLIDE = '.tc-website-image-scroll-stack__slide';
const HEADER_ACTIVE_CLASS = 'has-image-scroll-stack-active';
const PAGE_START_CLASS = 'has-image-scroll-stack-page-start';

const bindHeaderToStackVisibility = (stacks) => {
  const visibleStacks = new Set();
  const preserveInitialActiveState = document.body.classList.contains(HEADER_ACTIVE_CLASS);
  let hasObserverMeasurement = false;

  const updateHeaderState = () => {
    if (!hasObserverMeasurement && preserveInitialActiveState) {
      return;
    }
    document.body.classList.toggle(HEADER_ACTIVE_CLASS, visibleStacks.size > 0);
  };

  const isElementVisibleInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  };

  const observer = new IntersectionObserver((entries) => {
    hasObserverMeasurement = true;
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleStacks.add(entry.target);
      } else {
        visibleStacks.delete(entry.target);
      }
    });
    updateHeaderState();
  }, {
    root: null,
    threshold: 0
  });

  stacks.forEach((stack) => {
    if (isElementVisibleInViewport(stack)) {
      visibleStacks.add(stack);
    }
    observer.observe(stack);
  });
  updateHeaderState();
};

/**
 * Molette : un geste = une image (snap sur la position de scroll correspondante).
 * Touch / barre de défilement : snap GSAP pour ne pas rester entre deux images.
 */
const bindDiscreteScroll = (stack, timeline) => {
  const st = timeline.scrollTrigger;
  if (!st) {
    return;
  }

  let scrollAnim = null;
  let touchStartY = null;
  let touchHandled = false;

  const getCurrentStepContext = () => {
    const y = window.scrollY;
    if (y < st.start - 2 || y > st.end + 2) {
      return null;
    }

    const slides = stack.querySelectorAll(SELECTOR_SLIDE);
    const segmentCount = Math.max(slides.length - 1, 1);
    const range = st.end - st.start;
    const progress = range > 0 ? (y - st.start) / range : 0;
    const idx = Math.round(progress * segmentCount);

    return {
      idx,
      range,
      segmentCount
    };
  };

  const scrollToStep = (nextIdx, segmentCount, range) => {
    const nextY = st.start + (range * nextIdx) / segmentCount;

    scrollAnim = gsap.to(window, {
      scrollTo: nextY,
      duration: 0.35,
      ease: 'power2.out',
      overwrite: 'auto',
      onComplete: () => {
        scrollAnim = null;
      }
    });
  };

  const moveByDirection = (dir) => {
    if (scrollAnim && scrollAnim.isActive()) {
      return false;
    }

    const stepContext = getCurrentStepContext();
    if (!stepContext) {
      return false;
    }

    const {
      idx,
      range,
      segmentCount
    } = stepContext;
    const nextIdx = Math.max(0, Math.min(segmentCount, idx + dir));

    // No último / primeiro passo: não bloquear o scroll — deixa sair da secção ou voltar ao conteúdo acima
    if (nextIdx === idx) {
      return false;
    }

    scrollToStep(nextIdx, segmentCount, range);
    return true;
  };

  stack.addEventListener('wheel', (e) => {
    if (e.deltaY === 0) {
      return;
    }

    const dir = e.deltaY > 0 ? 1 : -1;
    if (moveByDirection(dir)) {
      e.preventDefault();
    }
  }, { passive: false });

  stack.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) {
      touchStartY = null;
      touchHandled = false;
      return;
    }
    touchStartY = e.touches[0].clientY;
    touchHandled = false;
  }, { passive: true });

  stack.addEventListener('touchmove', (e) => {
    if (touchStartY === null || e.touches.length !== 1) {
      return;
    }

    if (scrollAnim && scrollAnim.isActive()) {
      e.preventDefault();
      return;
    }

    if (touchHandled) {
      e.preventDefault();
      return;
    }

    const deltaY = e.touches[0].clientY - touchStartY;
    if (Math.abs(deltaY) < 24) {
      return;
    }

    // Swipe vers le haut -> image suivante ; swipe vers le bas -> image précédente
    const dir = deltaY < 0 ? 1 : -1;
    if (moveByDirection(dir)) {
      touchHandled = true;
      e.preventDefault();
    }
  }, { passive: false });

  stack.addEventListener('touchend', () => {
    touchStartY = null;
    touchHandled = false;
  }, { passive: true });
};

const initStack = (stack) => {
  const items = Array.from(stack.querySelectorAll(SELECTOR_ITEM));
  const slides = items
    .map((item) => item.querySelector(SELECTOR_SLIDE))
    .filter(Boolean);

  if (!slides.length) {
    return;
  }

  const segmentCount = Math.max(slides.length - 1, 1);

  const scrollTriggerConfig = {
    trigger: stack,
    start: 'top top',
    end: `+=${Math.max(slides.length + 1, 2) * window.innerHeight}`,
    scrub: true,
    pin: true,
    pinSpacing: true,
    invalidateOnRefresh: true
  };

  if (slides.length > 1) {
    scrollTriggerConfig.snap = {
      snapTo: (value) => Math.round(value * segmentCount) / segmentCount,
      duration: { min: 0.2, max: 0.45 },
      ease: 'power2.inOut'
    };
  }

  const tl = gsap.timeline({
    scrollTrigger: scrollTriggerConfig
  });

  slides.forEach((slide, index) => {
    gsap.set(slide, {
      zIndex: index + 1,
      yPercent: index === 0 ? 0 : 100
    });
  });

  slides.slice(1).forEach((slide) => {
    tl.to(slide, { yPercent: 0, ease: 'none', duration: 1 });
  });

  if (slides.length > 1) {
    bindDiscreteScroll(stack, tl);
  }
};

const init = () => {
  const stacks = Array.from(document.querySelectorAll(SELECTOR_STACK));
  if (!stacks.length) {
    return;
  }

  const firstStackRect = stacks[0].getBoundingClientRect();
  const stackStartsNearTop = firstStackRect.top <= window.innerHeight * 0.35;
  if (stackStartsNearTop) {
    document.body.classList.add(PAGE_START_CLASS);
    if (window.scrollY === 0) {
      document.body.classList.add(HEADER_ACTIVE_CLASS);
    }
  }

  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
  bindHeaderToStackVisibility(stacks);
  stacks.forEach(initStack);
  ScrollTrigger.refresh();
};

export default { init };
